import React from 'react';
import {
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight
} from 'react-icons/fa';

export interface PaginationType {
  offset: number;
  limit: number;
  page: number;
  total_pages: number;
  total_count: number;
  has_next_page: boolean;
  has_prev_page: boolean;
  next_page: number | null;
  prev_page: number | null;
}

interface PaginationProps {
  pagination: PaginationType;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  showInfo?: boolean;
  maxVisiblePages?: number;
  limitOptions?: number[];
}

export const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  onLimitChange,
  showInfo = true,
  maxVisiblePages = 5,
  limitOptions = [10, 25, 50, 100]
}) => {
  const {
    page: current_page,
    total_pages,
    total_count: total,
    limit: per_page,
    has_next_page,
    has_prev_page
  } = pagination;

  const perPageValue = per_page || limitOptions[0];
  const limitValues = Array.from(new Set([...limitOptions, perPageValue])).sort((a, b) => a - b);

  // Don't render pagination if there's no data
  if (total === 0) {
    return null;
  }

  const renderPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, current_page - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);
    const adjustedStartPage = Math.max(1, endPage - maxVisiblePages + 1);

    // Add first page and ellipsis if needed
    if (adjustedStartPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => onPageChange(1)}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 border border-gray-300 rounded  transition-colors"
        >
          1
        </button>
      );

      if (adjustedStartPage > 2) {
        pages.push(
          <span key="ellipsis1" className="px-2 py-1.5 text-sm text-gray-500">
            ...
          </span>
        );
      }
    }

    // Add visible page numbers
    for (let i = adjustedStartPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${i === current_page
              ? 'bg-blue-800 text-white shadow-sm'
              : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
        >
          {i}
        </button>
      );
    }

    // Add last page and ellipsis if needed
    if (endPage < total_pages) {
      if (endPage < total_pages - 1) {
        pages.push(
          <span key="ellipsis2" className="px-2 py-1.5 text-sm text-gray-500">
            ...
          </span>
        );
      }

      pages.push(
        <button
          key={total_pages}
          onClick={() => onPageChange(total_pages)}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 border border-gray-300 rounded  transition-colors"
        >
          {total_pages}
        </button>
      );
    }

    return pages;
  };

  const startItem = total === 0 ? 0 : ((current_page - 1) * perPageValue) + 1;
  const endItem = total === 0 ? 0 : Math.min(current_page * perPageValue, total);

  return (
    <div className="px-4 py-3 bg-white border-t border-gray-200">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="flex flex-col sm:flex-row items-center gap-3 order-2 sm:order-1 w-full sm:w-auto">
          {showInfo && (
            <div className="text-xs text-gray-700 text-center sm:text-left">
              Showing <span className="font-medium">{startItem}</span> to{' '}
              <span className="font-medium">{endItem}</span> of{' '}
              <span className="font-medium">{total}</span> results
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-gray-700">
            <span className="text-gray-500">Rows per page</span>
            <select
              value={perPageValue}
              onChange={(e) => onLimitChange?.(Number(e.target.value))}
              disabled={!onLimitChange}
              className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
            >
              {limitValues.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        {total_pages > 1 && (
          <div className="flex items-center gap-1 order-1 sm:order-2">
            <button
              onClick={() => onPageChange(1)}
              disabled={!has_prev_page}
              className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent rounded transition-colors"
              title="First page"
            >
              <FaAngleDoubleLeft className="w-3 h-3" />
            </button>

            <button
              onClick={() => onPageChange(current_page - 1)}
              disabled={!has_prev_page}
              className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent rounded transition-colors"
              title="Previous page"
            >
              <FaChevronLeft className="w-3 h-3" />
            </button>

            <div className="flex items-center gap-1 mx-2">
              {renderPageNumbers()}
            </div>

            <button
              onClick={() => onPageChange(current_page + 1)}
              disabled={!has_next_page}
              className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent rounded transition-colors"
              title="Next page"
            >
              <FaChevronRight className="w-3 h-3" />
            </button>

            <button
              onClick={() => onPageChange(total_pages)}
              disabled={!has_next_page}
              className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent rounded transition-colors"
              title="Last page"
            >
              <FaAngleDoubleRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Mobile-friendly pagination info */}
      {total_pages > 1 && (
        <div className="sm:hidden mt-2 text-center">
          <span className="text-xs text-gray-500">
            Page {current_page} of {total_pages}
          </span>
        </div>
      )}
    </div>
  );
};